/**
 * Network utility functions
 */

// Import Capacitor plugins
import { Capacitor } from "@capacitor/core";
import { Network } from "@capacitor/network";

// Track offline status
let _isOffline = !navigator.onLine; // Initialize with browser value
let _forcedOfflineState = null;
let _lastConnectivityCheck = 0;
let _lastConnectivityResult = null;
const CONNECTIVITY_CHECK_INTERVAL = 30000; // 30 seconds

/**
 * Check if we're running on Android
 */
export const isAndroid = () => {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
};

/**
 * Force the application into a specific offline mode state
 * @param {boolean} offlineState - true to force offline, false to force online, null to use automatic detection
 */
export const forceOfflineMode = (offlineState) => {
  _forcedOfflineState = offlineState;
};

/**
 * Try to fetch a small resource to test actual internet connectivity
 * This is more reliable than navigator.onLine as it tests actual internet access
 */
const testRealConnectivity = async () => {
  const now = Date.now();

  // Use cached result if checked recently
  if (
    now - _lastConnectivityCheck < CONNECTIVITY_CHECK_INTERVAL &&
    _lastConnectivityResult !== null
  ) {
    return _lastConnectivityResult;
  }

  try {
    // Try a fetch to a reliable service - with cache busting
    const testUrls = [
      "https://www.google.com/generate_204", // Google connectivity test
      "https://www.cloudflare.com/cdn-cgi/trace", // Cloudflare
      "https://httpbin.org/status/200", // Httpbin
    ];

    // Try each URL in sequence until one succeeds or all fail
    for (const url of testUrls) {
      try {
        const testUrl = `${url}?_=${now}`; // Cache busting
        const response = await Promise.race([
          fetch(testUrl, {
            method: "HEAD",
            mode: "no-cors",
            cache: "no-store",
            headers: { "Cache-Control": "no-cache" },
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Timeout")), 5000)
          ),
        ]);

        _lastConnectivityCheck = now;
        _lastConnectivityResult = true;
        return true;
      } catch (err) {
        // Continue to next URL
      }
    }

    // If we get here, all tests failed
    _lastConnectivityCheck = now;
    _lastConnectivityResult = false;
    return false;
  } catch (error) {
    _lastConnectivityCheck = now;
    _lastConnectivityResult = false;
    return false;
  }
};

/**
 * Update network status based on platform
 */
const updateNetworkStatus = async () => {
  try {
    // First check the basic network state from the platform
    let basicNetworkState = navigator.onLine;

    if (Capacitor.isPluginAvailable("Network")) {
      const status = await Network.getStatus();
      basicNetworkState = status.connected;
    }

    // If device says it's offline, trust it
    if (!basicNetworkState) {
      _isOffline = true;
      return true; // Device is definitely offline
    }

    // If device says it's online, verify with an actual connection test
    const canReachInternet = await testRealConnectivity();
    _isOffline = !canReachInternet;

    return _isOffline;
  } catch (error) {
    _isOffline = !navigator.onLine; // Fallback
    return _isOffline;
  }
};

// Initial network status check
updateNetworkStatus();

// Register network status change listeners
export const registerNetworkListeners = (onOffline, onOnline) => {
  // Browser listeners
  window.addEventListener("offline", () => {
    _isOffline = true;
    if (onOffline) onOffline();
  });

  window.addEventListener("online", async () => {
    // Double check with a real connectivity test
    const isReallyOffline = !(await testRealConnectivity());
    _isOffline = isReallyOffline;

    if (!isReallyOffline && onOnline) onOnline();
  });

  // Capacitor listeners (more reliable on mobile)
  if (Capacitor.isPluginAvailable("Network")) {
    Network.addListener("networkStatusChange", async (status) => {
      // If disconnected, definitely offline
      if (!status.connected) {
        _isOffline = true;
        if (onOffline) onOffline();
        return;
      }

      // If connected, verify with a real connection test
      const isReallyOffline = !(await testRealConnectivity());
      _isOffline = isReallyOffline;

      if (!isReallyOffline && onOnline) onOnline();
    });
  }
};

/**
 * Check if the app is in offline mode
 * @returns {boolean} True if the app is offline
 */
export const isOfflineMode = () => {
  // If we have a forced state, use that
  if (_forcedOfflineState !== null) {
    return _forcedOfflineState;
  }

  // For Android, periodically update the network status
  const now = Date.now();
  if (now - _lastConnectivityCheck > CONNECTIVITY_CHECK_INTERVAL) {
    // Schedule an update but don't wait for it
    updateNetworkStatus().catch((e) => {});
  }

  return _isOffline;
};

/**
 * Force a network status check and return the result
 * @returns {Promise<boolean>} Promise resolving to true if offline
 */
export const checkNetworkStatus = async () => {
  return await updateNetworkStatus();
};
